import React, { useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useMutation } from "react-query";
import { Formik, Form } from "formik";
import * as yup from "yup";
import styled from "styled-components";
// import { verifyOTP } from "services/apiSrv";
import { verifyOTP } from "services/authSrv";

// Styled components (unchanged from your code)
const SplitScreenContainer = styled.div`
  display: flex;
  height: 100vh;
  width: 100vw;
  overflow: hidden;
  background-color: #111827;
`;

const LeftPanel = styled.div`
  flex: 1;
  background: #003049;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 2.5rem;
  font-weight: bold;
  text-align: center;
  padding: 2rem;
`;

const RightPanel = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #111827;
`;

const VerifyCodeCard = styled.div`
  width: 100%;
  max-width: 28rem;
  padding: 2rem;
`;

const Logo = styled.img`
  margin: 0 auto;
  height: 2.5rem;
  width: auto;
`;

const Title = styled.h2`
  margin-top: 2.5rem;
  text-align: center;
  font-size: 1.5rem;
  line-height: 2.25rem;
  font-weight: 700;
  color: white;
`;

const StyledForm = styled(Form)`
  margin-top: 2.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  line-height: 1.5rem;
  font-weight: 500;
  color: #f3f4f6;
`;

const OtpContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  margin-top: 0.5rem;
`;

const OtpInput = styled.input`
  width: 3rem;
  height: 3rem;
  text-align: center;
  border-radius: 0.375rem;
  background-color: rgba(255, 255, 255, 0.05);
  color: white;
  font-size: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  outline-offset: -1px;
  transition: all 0.2s;
  &:focus {
    outline: 2px solid #6366f1;
    outline-offset: -2px;
  }
  &::placeholder {
    color: #6b7280;
  }
`;

const ErrorText = styled.div`
  margin-top: 0.25rem;
  color: #dc2626;
  font-size: 0.75rem;
  text-align: center;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
`;

const SubmitButton = styled.button`
  display: flex;
  width: 100%;
  justify-content: center;
  padding: 0.375rem 0.75rem;
  border-radius: 0.375rem;
  background-color: #6366f1;
  color: white;
  font-size: 0.875rem;
  line-height: 1.5rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s;
  &:hover {
    background-color: #4f46e5;
  }
  &:disabled {
    background-color: #6b7280;
    cursor: not-allowed;
  }
`;

const BackButton = styled.button`
  display: flex;
  width: 100%;
  justify-content: center;
  padding: 0.375rem 0.75rem;
  border-radius: 0.375rem;
  background-color: #6b7280;
  color: white;
  font-size: 0.875rem;
  line-height: 1.5rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s;
  &:hover {
    background-color: #4b5563;
  }
`;

const VerifyOtp = () => {
  const history = useHistory();
  const [otp, setOtp] = useState(["", "", "", ""]);
  const inputRefs = useRef([]);

  // Validation schema for OTP
  const validationSchema = yup.object({
    code: yup
      .string()
      .length(4, "Code must be exactly 4 digits")
      .matches(/^\d{4}$/, "Code must contain only digits")
      .required("Code is required"),
  });

  // useMutation for verifying OTP
  const mutation = useMutation({
    mutationFn: (code) => verifyOTP({ code, regId: localStorage.getItem("regId") }), // Assuming verifyOTP accepts { code }
    onSuccess: () => {
      history.push("/credentials");
    },
    onError: (error) => {
      console.error("Error verifying OTP:", error);
      // Optionally set an error state to display to the user
    },
  });

  const handleInputChange = (index, value) => {
    if (/^\d?$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Move focus to next input
      if (value && index < 3) {
        inputRefs.current[index + 1].focus();
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    const pastedData = e.clipboardData.getData("text").trim();
    if (/^\d{4}$/.test(pastedData)) {
      const newOtp = pastedData.split("");
      setOtp(newOtp);
      inputRefs.current[3].focus();
    }
    e.preventDefault();
  };

  return (
    <SplitScreenContainer>
      <LeftPanel>Verify Your Code</LeftPanel>
      <RightPanel>
        <VerifyCodeCard>
          <Logo
            src="https://tailwindcss.com/plus-assets/img/logos/mark.svg?color=indigo&shade=500"
            alt="Your Company"
          />
          <Title>Enter Verification Code</Title>
          <Formik
            initialValues={{ code: "" }}
            validationSchema={validationSchema}
            onSubmit={(values, { setSubmitting }) => {
              const code = otp.join("");
              mutation.mutate(code); // Trigger the mutation
              setSubmitting(false);
            }}
          >
            {({ isSubmitting, setFieldValue, errors, touched }) => (
              <StyledForm>
                <FormGroup>
                  <Label>Verification Code</Label>
                  <OtpContainer>
                    {otp.map((digit, index) => (
                      <OtpInput
                        key={index}
                        type="text"
                        maxLength="1"
                        value={digit}
                        onChange={(e) => {
                          handleInputChange(index, e.target.value);
                          setFieldValue("code", otp.join("") + e.target.value);
                        }}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        onPaste={index === 0 ? handlePaste : null}
                        ref={(el) => (inputRefs.current[index] = el)}
                        aria-label={`Digit ${index + 1} of verification code`}
                      />
                    ))}
                  </OtpContainer>
                  {touched.code && errors.code && (
                    <ErrorText>{errors.code}</ErrorText>
                  )}
                  {mutation.isError && (
                    <ErrorText>
                      {mutation.error.message || "Failed to verify OTP"}
                    </ErrorText>
                  )}
                </FormGroup>
                <ButtonGroup>
                  <SubmitButton
                    type="submit"
                    disabled={isSubmitting || mutation.isLoading}
                  >
                    {mutation.isLoading ? "Processing..." : "Submit"}
                  </SubmitButton>
                  <BackButton
                    type="button"
                    onClick={() => history.push("/forgot-password")}
                  >
                    Back
                  </BackButton>
                </ButtonGroup>
              </StyledForm>
            )}
          </Formik>
        </VerifyCodeCard>
      </RightPanel>
    </SplitScreenContainer>
  );
};

export default VerifyOtp;